import { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ErrorBoundary } from '@bonnet/next/error-boundary';

import { getMarket } from '@atc/bonnet-reference';
import { useFeatures } from '@atc/features';

import {
    ColoredBackground,
    Panel,
} from 'reaxl';

import { getExtendedHomeDelivery } from '@/actions/vdp/remoteActions';

import userDuck from '@/ducks/userDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpExtendedHomeDeliveryDuck from '@/ducks/vdp/vdpExtendedHomeDeliveryDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import OwnerDetailsSnapshot from '@/components/OwnerDetailsSnapshot/index.js';

/**
 * Owner details component that displays the owner information and methods to
 * contact them. This includes: chat, text, email, address, owner name, and phone number.
 * It can be combined with email, display in a panel and have a colored background.
 * @param listing               contains all of the listing information needed for the component, i.e. owner
 *                              info
 * @param uiContext             determines if panel component and if colored background should show
 * @param onEmailCTAClick       property for email button
 * @param dealerZip             dealer zip
 * @param actions               call to actions
 */
export function OwnerDetailsSnapshotContainer({
    disableContactCTAs = false,
    onEmailCTAClick,
    uiContext,
    ...rest
}) {
    const dispatch = useDispatch();
    const {
        dealer_rating: [isDealerRatingEnabled],
        dealer_name_relocation: [isDealerNameRelocationFlagEnabled],
        vdp_extended_home_delivery: [enableExtendedHomeDelivery],
    } = useFeatures(['dealer_rating', 'dealer_name_relocation', 'vdp_extended_home_delivery']);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const { owner = {}, vin } = listing ?? {};

    const extendedHomeDeliveryState = useSelector(vdpExtendedHomeDeliveryDuck.selectors.getDuckState);
    const { extendedHomeDelivery = null } = extendedHomeDeliveryState ?? {};

    const userZip = useSelector(userDuck.selectors.getZip);
    const showKBBDealerModal = useSelector(vdpActiveInteractionDuck.selectors.getShowKBBDealerModal);

    const ecommHrefWithWalletData = useSelector(vdpResultsDuck.selectors.getEcommHrefWithWalletData);

    const deliveryQuoteClick = useRef(false);
    const [estimateLoading, setEstimateLoading] = useState(false);
    // Handle empty panel or background color
    const [isEmptySnapshot, setIsEmptySnapshot] = useState(false);

    useEffect(() => {
        if (extendedHomeDelivery !== null) {
            setEstimateLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(extendedHomeDelivery)]);

    const {
        location: {
            address: {
                zip: dealerZip = '',
            } = {},
        } = {},
    } = owner;

    const {
        isPanel,
        showColoredBackground,
    } = uiContext ?? {};

    const listingOwner = {
        ...owner,
        rating: isDealerRatingEnabled ? owner.rating : undefined,
    };

    const ownerDetails = {
        listing: {
            ...listing,
            owner: listingOwner,
            ...(ecommHrefWithWalletData && {
                ecommWebsite: {
                    ...listing.ecommWebsite,
                    href: ecommHrefWithWalletData,
                },
            }),
        },
        enableDealerAvatar: true, // we need to keep this attribute on atc-ui because another place use it without this attribute
        ...rest,
    };

    const getQuickEstimates = useCallback((searchZip) => {
        if (enableExtendedHomeDelivery && dealerZip && (userZip || searchZip)) {
            dispatch(getExtendedHomeDelivery({ dealerZip, zip: searchZip || userZip, vin }));
            deliveryQuoteClick.current = true;
            setEstimateLoading(true);
        }
    }, [dispatch, dealerZip, enableExtendedHomeDelivery, userZip, vin]);

    const extendedHomeDeliveryProps = {
        extendedHomeDelivery: extendedHomeDelivery ?? {},
        getQuickEstimates,
        deliveryQuoteClick: deliveryQuoteClick.current,
        onEmailCTAClick,
        userZip,
        getMarket,
        isLoading: estimateLoading,
    };

    const handleEmptySnapshot = () => {
        setIsEmptySnapshot(true);
    };

    let snapshot = (
        <ErrorBoundary>
            <OwnerDetailsSnapshot
                {...ownerDetails}
                defaultShowModal={showKBBDealerModal}
                enableRatingImpression
                extendedHomeDeliveryProps={extendedHomeDeliveryProps}
                handleEmptySnapshot={handleEmptySnapshot}
                hideDealerName={isDealerNameRelocationFlagEnabled && !owner.privateSeller}
                disableContactCTAs={disableContactCTAs}
                showAddress
            />
        </ErrorBoundary>
    );

    // If empty snapshot, should NOT render snapshot with 'panel' or 'background color'
    if (isEmptySnapshot) {
        return null;
    }

    if (isPanel) {
        snapshot = (<Panel.Body>{snapshot}</Panel.Body>);
    }

    if (showColoredBackground) {
        snapshot = (<ColoredBackground uiContext="info">{snapshot}</ColoredBackground>);
    }

    return isPanel ? (<Panel className="margin-top-4">{snapshot}</Panel>) : snapshot;
}

export default OwnerDetailsSnapshotContainer;
